/**
 * AdminAccounts page (/admin/accounts)
 *
 * Manages admins who can sign in at /admin/login.
 *   - The "Owner" row is the env-var ADMIN_EMAIL/ADMIN_PASSWORD (immutable here).
 *   - Additional admins are stored in the `admin_accounts` MongoDB collection
 *     with bcrypt-hashed passwords.
 *
 * Provides:
 *   - List of all admins with metadata (added, added-by, last sign-in)
 *   - "Add administrator" dialog (email + password + confirm + label)
 *   - Per-row "Edit" dialog (rename label, reset password)
 *   - Per-row "Delete" confirmation (Owner protected; self-delete blocked)
 *   - "Change my password" panel (requires current password)
 */
import { useEffect, useState } from "react";
import { ShieldUser, Plus, Pencil, Trash2, KeyRound, Save, X, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDateTime } from "@/lib/format";

const emptyCreate = { email: "", password: "", password_confirm: "", label: "" };
const emptyEdit = { id: "", email: "", label: "", new_password: "", password_confirm: "" };
const emptyChangePw = { current_password: "", new_password: "", password_confirm: "" };

export default function AdminAccounts() {
  const { t, lang } = useI18n();
  const tx = t.admin.accountsPage;
  const [me, setMe] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState(emptyCreate);
  const [creating, setCreating] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editDraft, setEditDraft] = useState(emptyEdit);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [pwDraft, setPwDraft] = useState(emptyChangePw);
  const [changingPw, setChangingPw] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [meResp, listResp] = await Promise.all([
        api.get("/admin/me"),
        api.get("/admin/accounts"),
      ]);
      setMe(meResp.data);
      setAccounts(listResp.data || []);
    } catch (err) {
      console.error("AdminAccounts: failed to load", err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  // ---- Create ----
  const submitCreate = async () => {
    const email = (createDraft.email || "").trim().toLowerCase();
    if (!email || !email.includes("@")) {
      toast.error(lang === "es" ? "Correo invalido" : "Invalid email");
      return;
    }
    if ((createDraft.password || "").length < 8) {
      toast.error(tx.passwordMin);
      return;
    }
    if (createDraft.password !== createDraft.password_confirm) {
      toast.error(tx.passwordMismatch);
      return;
    }
    setCreating(true);
    try {
      await api.post("/admin/accounts", {
        email,
        password: createDraft.password,
        label: createDraft.label || "",
      });
      toast.success(tx.createdToast);
      setCreateOpen(false);
      setCreateDraft(emptyCreate);
      reload();
    } catch (err) {
      const detail = err?.response?.data?.detail || t.common.error;
      toast.error(String(detail));
    } finally {
      setCreating(false);
    }
  };

  // ---- Edit ----
  const openEdit = (acc) => {
    setEditDraft({
      id: acc.id, email: acc.email, label: acc.label || "",
      new_password: "", password_confirm: "",
    });
    setEditOpen(true);
  };
  const submitEdit = async () => {
    const payload = { label: editDraft.label };
    if (editDraft.new_password) {
      if (editDraft.new_password.length < 8) {
        toast.error(tx.passwordMin);
        return;
      }
      if (editDraft.new_password !== editDraft.password_confirm) {
        toast.error(tx.passwordMismatch);
        return;
      }
      payload.new_password = editDraft.new_password;
    }
    setSavingEdit(true);
    try {
      await api.put(`/admin/accounts/${editDraft.id}`, payload);
      toast.success(tx.savedToast);
      setEditOpen(false);
      reload();
    } catch (err) {
      toast.error(String(err?.response?.data?.detail || t.common.error));
    } finally {
      setSavingEdit(false);
    }
  };

  // ---- Delete ----
  const submitDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/accounts/${deleteTarget.id}`);
      toast.success(tx.deletedToast);
      setDeleteTarget(null);
      reload();
    } catch (err) {
      toast.error(String(err?.response?.data?.detail || t.common.error));
    } finally {
      setDeleting(false);
    }
  };

  // ---- Change my password ----
  const submitChangePw = async () => {
    if ((pwDraft.new_password || "").length < 8) {
      toast.error(tx.passwordMin);
      return;
    }
    if (pwDraft.new_password !== pwDraft.password_confirm) {
      toast.error(tx.passwordMismatch);
      return;
    }
    setChangingPw(true);
    try {
      await api.post("/admin/accounts/change-my-password", {
        current_password: pwDraft.current_password,
        new_password: pwDraft.new_password,
      });
      toast.success(tx.passwordChanged);
      setPwDraft(emptyChangePw);
    } catch (err) {
      toast.error(String(err?.response?.data?.detail || t.common.error));
    } finally {
      setChangingPw(false);
    }
  };

  const isOwnerMe = !!me?.is_owner;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <h1 className="font-serif-display text-2xl font-semibold flex items-center gap-2">
            <ShieldUser className="h-5 w-5 text-primary" />
            {tx.pageTitle}
          </h1>
          <p className="text-sm text-muted-foreground">{tx.pageSubtitle}</p>
        </div>
        <Button
          onClick={() => { setCreateDraft(emptyCreate); setCreateOpen(true); }}
          data-testid="admin-accounts-add"
        >
          <Plus className="mr-1 h-4 w-4" />
          {tx.addAdmin}
        </Button>
      </div>

      {/* Accounts list */}
      <Card className="rounded-2xl ring-1 ring-black/5 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={`acc-skel-${i}`} className="h-16 w-full rounded-xl" />
              ))}
            </div>
          ) : (
            <ul className="divide-y divide-border" data-testid="admin-accounts-list">
              {accounts.map((a) => {
                const isOwnerRow = !!a.is_owner;
                const isMeRow = (me?.email || "").toLowerCase() === (a.email || "").toLowerCase();
                return (
                  <li
                    key={a.id}
                    className="flex items-center gap-3 px-4 py-3"
                    data-testid={`admin-account-row-${a.id}`}
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-semibold">
                      {(a.label || a.email || "?").trim().charAt(0).toUpperCase()}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-sm truncate">{a.email}</p>
                        {isOwnerRow ? (
                          <Badge variant="default" className="text-[10px] gap-1" data-testid="badge-owner">
                            <ShieldUser className="h-3 w-3" />
                            {tx.ownerBadge}
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-[10px]">
                            {tx.adminBadge}
                          </Badge>
                        )}
                        {isMeRow && (
                          <Badge variant="outline" className="text-[10px]">
                            {lang === "es" ? "Tu" : "You"}
                          </Badge>
                        )}
                      </div>
                      {a.label && a.label !== "Owner" && (
                        <p className="text-xs text-muted-foreground truncate">{a.label}</p>
                      )}
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {a.created_at
                          ? <>{tx.addedAt} {fmtDateTime(a.created_at, lang)}{a.created_by ? <> · {tx.addedBy} {a.created_by}</> : null}</>
                          : isOwnerRow
                            ? tx.ownerHint
                            : null}
                        {a.last_login_at && <> · {tx.lastLogin} {fmtDateTime(a.last_login_at, lang)}</>}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {!isOwnerRow && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEdit(a)}
                            aria-label="edit"
                            data-testid={`edit-admin-${a.id}`}
                            title={lang === "es" ? "Editar" : "Edit"}
                          >
                            <Pencil className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                          </Button>
                          {!isMeRow && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDeleteTarget(a)}
                              aria-label="delete"
                              data-testid={`delete-admin-${a.id}`}
                              title={lang === "es" ? "Eliminar" : "Delete"}
                            >
                              <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Change my password section */}
      <Card className="rounded-2xl ring-1 ring-black/5 shadow-sm" data-testid="change-my-password-card">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start gap-2">
            <KeyRound className="h-4 w-4 mt-0.5 text-primary" />
            <div className="min-w-0">
              <p className="font-serif-display text-base font-semibold">{tx.changeMyPasswordTitle}</p>
              <p className="text-xs text-muted-foreground">{tx.changeMyPasswordSub}</p>
            </div>
          </div>
          {isOwnerMe ? (
            <div className="rounded-xl bg-muted/40 p-3 text-sm text-muted-foreground flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{tx.ownerCantChangePassword}</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label={tx.currentPassword}>
                <Input
                  type="password"
                  autoComplete="current-password"
                  value={pwDraft.current_password}
                  onChange={(e) => setPwDraft({ ...pwDraft, current_password: e.target.value })}
                  data-testid="pw-current"
                />
              </Field>
              <Field label={tx.newPassword}>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={pwDraft.new_password}
                  onChange={(e) => setPwDraft({ ...pwDraft, new_password: e.target.value })}
                  data-testid="pw-new"
                />
              </Field>
              <Field label={tx.confirmPasswordLabel}>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={pwDraft.password_confirm}
                  onChange={(e) => setPwDraft({ ...pwDraft, password_confirm: e.target.value })}
                  data-testid="pw-confirm"
                />
              </Field>
              <div className="sm:col-span-3 flex justify-end">
                <Button
                  onClick={submitChangePw}
                  disabled={changingPw || !pwDraft.current_password || !pwDraft.new_password}
                  data-testid="pw-submit"
                >
                  {changingPw
                    ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" />{lang === "es" ? "Cambiando..." : "Updating..."}</>
                    : <><Save className="mr-1 h-4 w-4" />{tx.changePasswordButton}</>}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-admin-dialog">
          <DialogHeader>
            <DialogTitle>{tx.addAdmin}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Field label={tx.emailLabel}>
              <Input
                type="email"
                inputMode="email"
                autoComplete="email"
                value={createDraft.email}
                onChange={(e) => setCreateDraft({ ...createDraft, email: e.target.value })}
                placeholder="pastor@example.com"
                data-testid="create-email"
              />
            </Field>
            <Field label={tx.labelLabel}>
              <Input
                value={createDraft.label}
                onChange={(e) => setCreateDraft({ ...createDraft, label: e.target.value })}
                placeholder="Pastor Jose"
                data-testid="create-label"
              />
              <p className="text-[11px] text-muted-foreground mt-1">{tx.labelHint}</p>
            </Field>
            <Field label={tx.passwordLabel}>
              <Input
                type="password"
                autoComplete="new-password"
                value={createDraft.password}
                onChange={(e) => setCreateDraft({ ...createDraft, password: e.target.value })}
                data-testid="create-password"
              />
              <p className="text-[11px] text-muted-foreground mt-1">{tx.passwordMin}</p>
            </Field>
            <Field label={tx.confirmPasswordLabel}>
              <Input
                type="password"
                autoComplete="new-password"
                value={createDraft.password_confirm}
                onChange={(e) => setCreateDraft({ ...createDraft, password_confirm: e.target.value })}
                data-testid="create-password-confirm"
              />
            </Field>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              <X className="mr-1 h-4 w-4" />{t.common.cancel}
            </Button>
            <Button onClick={submitCreate} disabled={creating} data-testid="create-submit">
              {creating
                ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" />{lang === "es" ? "Creando..." : "Creating..."}</>
                : <><Save className="mr-1 h-4 w-4" />{t.common.save}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-admin-dialog">
          <DialogHeader>
            <DialogTitle>{tx.editTitle}</DialogTitle>
            <DialogDescription className="text-xs">{editDraft.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Field label={tx.labelLabel}>
              <Input
                value={editDraft.label}
                onChange={(e) => setEditDraft({ ...editDraft, label: e.target.value })}
                data-testid="edit-label"
              />
            </Field>
            <Field label={tx.newPassword}>
              <Input
                type="password"
                autoComplete="new-password"
                value={editDraft.new_password}
                placeholder={lang === "es" ? "Dejar vacio para no cambiar" : "Leave empty to keep current"}
                onChange={(e) => setEditDraft({ ...editDraft, new_password: e.target.value })}
                data-testid="edit-new-password"
              />
              <p className="text-[11px] text-muted-foreground mt-1">{tx.passwordMin}</p>
            </Field>
            {editDraft.new_password && (
              <Field label={tx.confirmPasswordLabel}>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={editDraft.password_confirm}
                  onChange={(e) => setEditDraft({ ...editDraft, password_confirm: e.target.value })}
                  data-testid="edit-password-confirm"
                />
              </Field>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              <X className="mr-1 h-4 w-4" />{t.common.cancel}
            </Button>
            <Button onClick={submitEdit} disabled={savingEdit} data-testid="edit-submit">
              {savingEdit
                ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" />{lang === "es" ? "Guardando..." : "Saving..."}</>
                : <><Save className="mr-1 h-4 w-4" />{t.common.save}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-admin-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              {tx.confirmDelete}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="rounded-lg bg-muted/40 p-3 mt-2 text-sm">
                <p className="font-medium text-foreground">{deleteTarget?.email}</p>
                {deleteTarget?.label && <p className="text-xs text-muted-foreground">{deleteTarget.label}</p>}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-admin-cancel">{t.common.cancel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={submitDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-admin-confirm"
            >
              <Trash2 className="mr-1 h-4 w-4" />
              {deleting ? (lang === "es" ? "Eliminando..." : "Removing...") : (lang === "es" ? "Eliminar" : "Remove")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
